import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {subMonths} from 'date-fns';
import {diApiEu} from '@di-shared/api/di';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import SafeDrivingMonthlyGraph from '@di-shared/features/safe-driving-vDSP/SafeDrivingMonthlyGraph';
import {findMinMaxDates, sortByPeriodEu} from '@di-shared/features/safe-driving-vDSP/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface SafeDrivingMonthlyGraphEuProps {
  vin: string;
}

const SCORE_THRESHOLD_70 = 70;

export default function ({vin}: SafeDrivingMonthlyGraphEuProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: SafeDrivingMonthlyGraphEuProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const today = new Date();
  /**
   * 최근 12개월을 한달 이전으로 미뤄서
   * 가령 오늘이 5월 14일이라면
   * To-Be 24.5 ~ 25.4
   */
  const startDate = getYYYYMMFormat(subMonths(today, 12));
  const endDate = getYYYYMMFormat(subMonths(today, 1));

  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.safeDriving.monthly1), vin, startDate, endDate],
    queryFn: () =>
      diApiEu.safeDriving.monthly1({
        vin,
        startMonth: startDate,
        endMonth: endDate,
      }),
  });

  const agreeDate = parseDateWithFormat(data.data.agreeDateTime);
  const isAgreedThisMonth = agreeDate && agreeDate.getMonth() === today.getMonth() && agreeDate.getFullYear() === today.getFullYear();

  // 동의월이 이번달이거나 데이터가 없는 경우 미노출 처리
  if (isAgreedThisMonth || !data.data.monthlyScoreList || data.data?.monthlyScoreList.length === 0) {
    return <></>;
  }

  const sortedData = sortByPeriodEu(data.data.monthlyScoreList);
  const {minDate, maxDate} = findMinMaxDates(sortedData.map(({scoreDateTime}) => scoreDateTime));
  const singleDate = parseDateWithFormat(sortedData[0]?.scoreDateTime); // 데이터가 한개만 있을 경우

  return (
    <div className="section_container safe_driving_monthy">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('ctn_dss_monthly_score_title_eu')}</h3>
      </div>
      <p className="section_container_desc">
        {sortedData.length === 1 && !!singleDate
          ? t('ctn_dss_monthly_score_content_only_eu', {date: CldrDatetimeFormatter.format(singleDate, 'yMMM')})
          : minDate &&
            maxDate &&
            t('ctn_dss_monthly_score_content_eu', {
              startDate: CldrDatetimeFormatter.format(minDate, 'yMMM'),
              endDate: CldrDatetimeFormatter.format(maxDate, 'yMMM'),
            })}
      </p>
      <SafeDrivingMonthlyGraph
        dashedLineScore={SCORE_THRESHOLD_70}
        coloredItemScoreThreshold={SCORE_THRESHOLD_70}
        items={sortedData.map(({safetyDrvScore, scoreDateTime}) => {
          const parsedDate = parseDateWithFormat(scoreDateTime);
          return {score: safetyDrvScore, label: parsedDate ? CldrDatetimeFormatter.format(parsedDate, 'MMM') : ''};
        })}
      />
      <p className="graph_notice_text">{t('msg_dss_monthly_recorded_eu')}</p>
    </div>
  );
}

function Loading() {
  return (
    <div
      className="section_container safe_driving_monthy skeleton"
      style={{height: 339}}
    />
  );
}
